import { useMemo } from 'react'

import { useLineChart } from './use-line-chart'

interface Props {
  dataKey: string
  noGradient?: boolean
  color?: string
  strokeWidth?: number
}

export const LineChartDataLine: React.FC<Props> = ({ dataKey, color, noGradient, strokeWidth = 2.5 }) => {
  const { dataSets, xScale, yScale } = useLineChart()

  const dataSet = dataSets[dataKey]

  const points = useMemo(() => {
    const points = dataSet.map((value, i) => {
      if (value === null) {
        return null
      }

      return {
        x: xScale(i),
        y: yScale(value),
      }
    })

    return points.filter((point) => point !== null)
  }, [dataSet, xScale, yScale])

  const linePath = useMemo(() => {
    return makeLinePath(points)
  }, [points])

  const gradientAreaPath = useMemo(() => {
    if (noGradient) {
      return ''
    }

    return makeGradientAreaPath(linePath, points)
  }, [linePath, points, noGradient])

  return (
    <g key={dataSet.length} className="duration-200 animate-in fade-in slide-in-from-bottom-1">
      {!noGradient && (
        <>
          <defs>
            <linearGradient id="lineGradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="rgba(0, 29, 144, 0.20)" />
              <stop offset="100%" stopColor="rgba(0, 28, 144, 0)" />
            </linearGradient>
          </defs>

          <path d={gradientAreaPath} fill="url(#lineGradient)" />
        </>
      )}

      <path d={linePath} fill="none" stroke={color ?? 'hsl(228, 100%, 28%)'} strokeWidth={strokeWidth} />
    </g>
  )
}

const makeLinePath = (points: { x: number; y: number }[]) => {
  return points.reduce((accum, point) => {
    const x = point.x
    const y = point.y

    return accum + (!accum ? `M ${x},${y}` : ` L ${x},${y}`)
  }, '')
}

const makeGradientAreaPath = (linePath: string, points: { x: number; y: number }[]) => {
  const BUFFER = 9

  if (points.length === 0) {
    return ''
  }

  const xStart = points[0].x
  const xEnd = points[points.length - 1].x
  const yMax = Math.max(...points.map((point) => point.y))

  return `${linePath} L ${xEnd},${yMax + BUFFER} L ${xStart},${yMax + BUFFER} Z`
}
